import React, { useState, useEffect } from "react";
import { Menubar } from "primereact/menubar";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";
import { MenuItem } from "primereact/menuitem";
import { getUserDisplayName, isAdmin, isFounder, canAccessRedTeam } from "../utils/authUtils";
import { getPreferredMsalAccount } from "../utils/msalAccountUtils";
import { GlobalSearchBar } from "./GlobalSearchBar";
import { useNotifications } from "../contexts/NotificationsContext";
import { useApiAuth } from "../hooks/useApiAuth";

export const Navigation: React.FC = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const account = getPreferredMsalAccount(instance, accounts);
  const { notificationCount } = useNotifications();
  const { appRoles } = useApiAuth();

  // Dark mode state - now supports system, light, dark
  const [themePreference, setThemePreference] = useState<
    "system" | "light" | "dark"
  >(() => {
    // Check localStorage for saved theme preference
    const saved = localStorage.getItem("themePreference");
    if (saved === "system" || saved === "light" || saved === "dark") {
      return saved;
    }
    return "system"; // Default to system theme
  });

  // Get actual dark mode state based on preference and system theme
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const updateTheme = () => {
      if (themePreference === "system") {
        setIsDarkMode(mediaQuery.matches);
      } else {
        setIsDarkMode(themePreference === "dark");
      }
    };

    // Initial update
    updateTheme();

    // Listen for system theme changes only if using system preference
    if (themePreference === "system") {
      mediaQuery.addEventListener("change", updateTheme);
      return () => mediaQuery.removeEventListener("change", updateTheme);
    }
  }, [themePreference]);

  // Apply dark mode theme
  useEffect(() => {
    const linkElement = document.getElementById(
      "theme-link",
    ) as HTMLLinkElement;
    if (linkElement) {
      if (isDarkMode) {
        linkElement.href = `${process.env.PUBLIC_URL || ""}/themes/primereact/lara-dark-indigo/theme.css`;
      } else {
        linkElement.href = `${process.env.PUBLIC_URL || ""}/themes/primereact/lara-light-indigo/theme.css`;
      }
    }

    // Toggle a root class so other components can detect dark mode reliably
    // This matches what ProjectPage checks ('.p-dark').
    document.documentElement.classList.toggle("p-dark", isDarkMode);
    document.body.classList.toggle("p-dark", isDarkMode);

    // Broadcast a global event so listeners can react immediately
    window.dispatchEvent(
      new CustomEvent("themechange", { detail: { isDarkMode } }),
    );

    // Save preference to localStorage
    localStorage.setItem("themePreference", themePreference);
  }, [isDarkMode, themePreference]);

  const toggleTheme = () => {
    // Cycle through: system -> light -> dark -> system
    if (themePreference === "system") {
      setThemePreference("light");
    } else if (themePreference === "light") {
      setThemePreference("dark");
    } else {
      setThemePreference("system");
    }
  };

  // Get theme icon and tooltip text
  const getThemeIcon = () => {
    if (themePreference === "system") {
      return "pi pi-desktop";
    }
    return isDarkMode ? "pi pi-moon" : "pi pi-sun";
  };

  const getThemeTooltip = () => {
    if (themePreference === "system") {
      return "Switch to light mode";
    } else if (themePreference === "light") {
      return "Switch to dark mode";
    } else {
      return "Switch to system theme";
    }
  };

  // Helper function to check if a path is active
  const isPathActive = (path: string): boolean => {
    const currentPath = location.pathname.toLowerCase();
    const targetPath = path.toLowerCase();

    // Remove trailing slashes for comparison
    const normalizedCurrent = currentPath.replace(/\/$/, "") || "/";
    const normalizedTarget = targetPath.replace(/\/$/, "") || "/";

    return (
      normalizedCurrent === normalizedTarget ||
      normalizedCurrent.startsWith(normalizedTarget + "/")
    );
  };

  // Build menu items based on user roles
  const menuItems: MenuItem[] = [
    {
      label: "Schedule",
      icon: "pi pi-calendar",
      command: () => navigate("/schedule"),
      className: isPathActive("/schedule") ? "p-focus" : "",
    },
    {
      label: "Timesheet",

      icon: "pi pi-clock",
      command: () => navigate("/timesheet"),
      className: isPathActive("/timesheet") ? "p-focus" : "",
    },
  ];

  if (canAccessRedTeam(appRoles)) {
    menuItems.push({
      label: "Red Team",
      icon: "pi pi-shield",
      command: () => navigate("/red-team"),
      className: isPathActive("/red-team") ? "p-focus" : "",
    });
  }

  // Add Founder menu if user has founder role
  if (isFounder(account)) {
    menuItems.push({
      label: "Founder",
      icon: "pi pi-crown",
      command: () => navigate("/founder"),
      className: isPathActive("/founder") ? "p-focus" : "",
    });
  }

  // Add Admin menu if user has admin role
  if (isAdmin(account)) {
    menuItems.push({
      label: "Admin",
      icon: "pi pi-cog",
      command: () => navigate("/admin"),
      className: isPathActive("/admin") ? "p-focus" : "",
    });
  }

  const endContent = (
    <div className="flex align-items-center gap-2">
      <GlobalSearchBar style={{ width: "500px" }} />
      <span className="text-sm text-600">
        Welcome, {getUserDisplayName(account)}
      </span>
      {notificationCount > 0 && (
        <Button
          icon="pi pi-bell"
          className="p-button-text p-button-sm "
          onClick={() => navigate("/timesheet")}
          tooltip="Go to timesheet"
          tooltipOptions={{ position: "bottom", style: { zIndex: 9999 } }}
        >
          <div
            className="p-overlay-badge"
            style={{ width: "0.2rem", height: "1rem" }}
          >
            {notificationCount > 0 && (
              <Badge value={notificationCount} severity="danger" />
            )}
          </div>
        </Button>
      )}
      <Button
        icon={getThemeIcon()}
        className="p-button-text p-button-sm"
        onClick={toggleTheme}
        tooltip={getThemeTooltip()}
        tooltipOptions={{ position: "bottom", style: { zIndex: 9999 } }}
        aria-label="Toggle theme"
      />
      {/* {process.env.NODE_ENV === "development" && (
        <Button
          label="Debug Roles"
          icon="pi pi-bug"
          className="p-button-outlined p-button-sm p-button-secondary"
          onClick={handleDebugRoles}
          tooltip="Debug ID token claims (development only)"
        />
      )} */}
      {/* <Button
        label="Logout"
        icon="pi pi-sign-out"
        className="p-button-outlined p-button-sm"
        onClick={handleLogout}
      /> */}
    </div>
  );

  const startContent = (
    <div className="flex align-items-center">
      <img
        src="/logo.png"
        alt="Backoffice Logo"
        className="h-2rem mr-2"
        style={{ height: "2rem" }}
      />
    </div>
  );

  return (
    <div className="card">
      <Menubar
        model={menuItems}
        start={startContent}
        end={endContent}
        className="border-none"
      />
    </div>
  );
};
